import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../../components/layout/MainLayout';
import { Clock, Loader2, X, FileText, Search as SearchIcon, MousePointerClick, AlertTriangle } from 'lucide-react';
import { auditApi } from '../../services/api';
import { toast } from 'sonner';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};
const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleTimeString('en-IN', { timeStyle: 'medium' }); }
  catch { return iso; }
};
const dur = (s) => {
  if (!s) return '0s';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60); const sec = s % 60;
  return `${m}m ${sec}s`;
};
const shortId = (id) => (id ? `${id.slice(0, 8)}…` : '—');

const eventIcon = (type) => {
  if (type === 'search') return SearchIcon;
  if (type === 'error') return AlertTriangle;
  if (type === 'document') return FileText;
  return MousePointerClick;
};

const SessionTimeline = () => {
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const data = await auditApi.getSessions('limit=100');
        if (active) { setSessions(data.items); setTotal(data.total); }
      } catch (err) {
        toast.error(err.message || 'Failed to load sessions');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  const openDetail = async (sessionId) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const data = await auditApi.getSession(sessionId);
      setDetail(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load session timeline');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <MainLayout title="Session Timeline" subtitle="Per-session activity & duration">
      <div data-testid="session-timeline-page">
        <div className="flex items-center gap-2 text-gray-500 mb-6">
          <Clock className="w-5 h-5" />
          <span className="text-sm">{total} sessions</span>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="session-loading">
              <Loader2 className="w-7 h-7 animate-spin text-blue-500" />
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8B] text-white">
                  <th className="px-6 py-4 text-left">Session</th>
                  <th className="px-6 py-4 text-left">User</th>
                  <th className="px-6 py-4 text-left">Pages</th>
                  <th className="px-6 py-4 text-left">Actions</th>
                  <th className="px-6 py-4 text-left">Errors</th>
                  <th className="px-6 py-4 text-left">Duration</th>
                  <th className="px-6 py-4 text-left">Started</th>
                </tr>
              </thead>
              <tbody data-testid="session-table-body">
                {sessions.length === 0 ? (
                  <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-500">No sessions tracked yet.</td></tr>
                ) : sessions.map((s, i) => (
                  <motion.tr
                    key={s.session_id}
                    data-testid={`session-row-${s.session_id}`}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                    onClick={() => openDetail(s.session_id)}
                    className="border-b border-gray-500/10 hover:bg-blue-500/5 cursor-pointer"
                  >
                    <td className="px-6 py-4 text-sm font-mono">{shortId(s.session_id)}</td>
                    <td className="px-6 py-4 text-sm">{s.is_registered ? (s.user_email || 'Registered') : 'Anonymous'}</td>
                    <td className="px-6 py-4 text-sm">{s.page_count}</td>
                    <td className="px-6 py-4 text-sm">{s.action_count}</td>
                    <td className="px-6 py-4 text-sm">{s.error_count > 0 ? <span className="text-red-500">{s.error_count}</span> : 0}</td>
                    <td className="px-6 py-4 text-sm">{dur(s.duration_seconds)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{fmt(s.start)}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          )}
        </motion.div>

        <AnimatePresence>
          {detail && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={() => setDetail(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                className="glass-card p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()} data-testid="session-detail-modal"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">Session Timeline</h3>
                  <button onClick={() => setDetail(null)} className="p-2 rounded-lg hover:bg-gray-500/10" data-testid="session-detail-close"><X className="w-5 h-5" /></button>
                </div>
                {detailLoading || detail.loading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
                ) : (
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-500">User:</span> {detail.session?.is_registered ? (detail.session?.user_email || 'Registered') : 'Anonymous'}</div>
                      <div><span className="text-gray-500">Duration:</span> {dur(detail.session?.duration_seconds)}</div>
                      <div><span className="text-gray-500">Pages:</span> {detail.session?.page_count}</div>
                      <div><span className="text-gray-500">Searches:</span> {detail.session?.search_count}</div>
                      <div><span className="text-gray-500">Actions:</span> {detail.session?.action_count}</div>
                      <div><span className="text-gray-500">Errors:</span> {detail.session?.error_count}</div>
                      <div><span className="text-gray-500">IP:</span> <span className="font-mono">{detail.session?.ip_address || '—'}</span></div>
                      <div><span className="text-gray-500">Started:</span> {fmt(detail.session?.start)}</div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-3">Timeline ({detail.events?.length || 0} events)</h4>
                      <div className="relative pl-6 space-y-3 max-h-72 overflow-y-auto">
                        {(detail.events || []).map((ev) => {
                          const Icon = eventIcon(ev.event_type);
                          return (
                            <div key={ev.id} className="relative">
                              <span className="absolute -left-6 top-1 w-3 h-3 rounded-full bg-blue-500" />
                              <div className="flex items-center justify-between text-xs">
                                <span className="flex items-center gap-2">
                                  <Icon className="w-3.5 h-3.5 text-blue-400" />
                                  <span className="text-blue-400">{ev.event_type}</span>
                                  <span className="text-gray-400">{ev.path || ''}</span>
                                </span>
                                <span className="text-gray-500 whitespace-nowrap">{fmtTime(ev.timestamp)}</span>
                              </div>
                            </div>
                          );
                        })}
                        {(!detail.events || detail.events.length === 0) && <p className="text-xs text-gray-500">No events in this session.</p>}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MainLayout>
  );
};

export default SessionTimeline;
